import { useQuery } from "@tanstack/react-query"
import { FlaskConical } from "lucide-react"

import { demoResetQueryOptions } from "@/lib/query-options"

function formatResetTime(value: string, timeZone: string) {
  return new Intl.DateTimeFormat("en-PH", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value))
}

const stateCopy = {
  scheduled: "Reset is scheduled.",
  running: "Reset in progress; demo writes are temporarily paused.",
  delayed: "Reset is taking longer than expected; demo writes remain paused.",
  failed: "The latest reset failed; demo writes remain paused for recovery.",
  overdue: "The scheduled reset is overdue; the displayed data may be delayed.",
  disabled: "Automatic reset is not active in this environment.",
  unavailable: "Reset status is temporarily unavailable.",
} as const

export function DemoNotice({ compact = false }: { compact?: boolean }) {
  const status = useQuery(demoResetQueryOptions())

  if (!status.data) {
    return (
      <section
        className="demo-notice"
        aria-label="Demo data reset"
        data-reset-state="loading"
      >
        <FlaskConical className="size-4 shrink-0" aria-hidden="true" />
        <span>
          <strong>Demo data reset.</strong> Reset schedule status is loading. Do
          not enter sensitive information. Shared changes may be visible to
          other visitors. No real payment is collected.
        </span>
      </section>
    )
  }

  const data = status.data

  return (
    <section
      className="demo-notice"
      aria-label="Demo data reset"
      data-reset-state={data.state}
      data-reset-generation={data.resetGeneration}
      data-fixture-version={data.fixtureVersion}
    >
      <FlaskConical className="size-4 shrink-0" aria-hidden="true" />
      <span>
        <strong>Demo data reset.</strong>{" "}
        {data.state === "disabled" ? (
          <>
            {stateCopy.disabled} The reviewed schedule contract is{" "}
            {data.scheduleLocalTime} Philippine Time ({data.scheduleTimeZone},{" "}
            {data.scheduleUtcOffset}).
          </>
        ) : (
          <>
            {compact
              ? "Daily reset: "
              : "Demo environment — The daily reset starts at "}
            {data.scheduleLocalTime} Philippine Time ({data.scheduleTimeZone},{" "}
            {data.scheduleUtcOffset}). {stateCopy[data.state]} Next reset:{" "}
            <time dateTime={data.nextScheduledResetAt}>
              {formatResetTime(
                data.nextScheduledResetAt,
                data.scheduleTimeZone
              )}
            </time>
            .
          </>
        )}{" "}
        Last completed:{" "}
        {data.lastSuccessfulResetAt ? (
          <time dateTime={data.lastSuccessfulResetAt}>
            {formatResetTime(data.lastSuccessfulResetAt, data.scheduleTimeZone)}
          </time>
        ) : (
          "No reset completed yet"
        )}
        . Shared changes may be visible to other visitors. No real payment is
        collected. Do not enter sensitive information.
      </span>
    </section>
  )
}
